// Default/placeholder values shown as '-' (matched case-insensitively)
const PLACEHOLDER_VALUES_LOWER = [
  'unknown client',
  'unknown device',
  'unknown model',
  'unknown brand',
  'unknown',
  'no description provided',
  'nema opisa',
  'nepoznat klijent',
  'nepoznat uređaj',
  'nepoznat model',
  'nepoznat brend',
  'nepoznato',
  'nema',
  'n/a',
  'n/d',
  'no phone',
  'unassigned'
];

/**
 * Replaces empty or default/placeholder values with '-'
 * @param {string} value - The value to check
 * @returns {string} - The value or '-' if it's empty/placeholder
 */
export const formatDisplayValue = (value) => {
  if (value === null || value === undefined) return '-';
  
  const normalizedValue = String(value).trim();
  if (!normalizedValue) return '-';
  
  const lower = normalizedValue.toLowerCase();
  if (PLACEHOLDER_VALUES_LOWER.includes(lower)) return '-';
  if (/^n\/a$/i.test(normalizedValue) || /^n\/d$/i.test(normalizedValue)) return '-';
  
  return normalizedValue;
};

/**
 * Format device info string, replacing placeholders with '-'
 * @param {string} deviceType - Device type
 * @param {string} deviceBrand - Device brand
 * @param {string} deviceModel - Device model
 * @returns {string} - Formatted device info string
 */
export const formatDeviceInfo = (deviceType, deviceBrand, deviceModel) => {
  const parts = [
    formatDisplayValue(deviceType),
    formatDisplayValue(deviceBrand),
    formatDisplayValue(deviceModel)
  ].filter(part => part !== '-');
  
  return parts.length > 0 ? parts.join(' ') : '-';
};

export const TERMINAL_JOB_STATUSES = ['Completed', 'Cancelled', 'Finished', 'Archived'];
const PENDING_QUEUE_STATUSES = ['In Pending', 'Received'];

/** Posao nije završen, otkazan niti arhiviran. */
export const isOpenActiveJob = (job) => {
  if (!job) return false;
  return !TERMINAL_JOB_STATUSES.includes(job.status);
};

/** Zakazani otvoreni posao (ima datum u kalendaru). */
export const isScheduledOpenJob = (job) => Boolean(job?.serviceDate) && isOpenActiveJob(job);

/** Nezakazani poslovi u redu čekanja. */
export const isPendingQueueJob = (job) =>
  !job?.serviceDate && PENDING_QUEUE_STATUSES.includes(job.status) && isOpenActiveJob(job);

/** Da li posao ima validan ISO/vremenski timestamp (ne prazan string). */
export const hasActiveJobTimestamp = (value) => {
  if (value === null || value === undefined || value === '') return false;
  const parsed = new Date(value);
  return !Number.isNaN(parsed.getTime());
};

/**
 * Posao je "u radu (trenutno)" samo ako tajmer za putovanje ili rad stvarno teče.
 * Status bez aktivnog startTime (pauziran) se ne broji.
 */
export const isJobTimerActive = (job) => {
  if (!job) return false;
  if (TERMINAL_JOB_STATUSES.includes(job.status)) return false;

  if (
    job.status === 'On Road' &&
    hasActiveJobTimestamp(job.travelStartTime)
  ) {
    return true;
  }

  if (
    (job.status === 'In Repair' || job.status === 'In Progress') &&
    hasActiveJobTimestamp(job.workStartTime)
  ) {
    return true;
  }

  return false;
};

/** Patch za Redux iz WebSocket `job_updated` događaja. */
export const buildJobUpdatesFromWebSocket = (data) => {
  if (!data) return null;

  const jobId = data.jobId ?? data.job?._id;
  if (!jobId) return null;

  const updates = {};
  const sources = [data.job, data].filter((s) => s && typeof s === 'object');

  sources.forEach((source) => {
    if (source.status !== undefined) updates.status = source.status;
    if ('travelStartTime' in source) updates.travelStartTime = source.travelStartTime;
    if ('workStartTime' in source) updates.workStartTime = source.workStartTime;
    if ('accumulatedTravelTime' in source) {
      updates.accumulatedTravelTime = source.accumulatedTravelTime ?? 0;
    }
    if ('accumulatedWorkTime' in source) {
      updates.accumulatedWorkTime = source.accumulatedWorkTime ?? 0;
    }
  });

  if (Object.keys(updates).length === 0) return null;

  return {
    jobId: String(jobId),
    updates,
    fullJob: data.job && typeof data.job === 'object' ? data.job : null
  };
};

/** Jedinstvena lista poslova sa aktivnim tajmerom (bez duplikata po _id). */
export const getJobsWithActiveTimer = (jobs) => {
  if (!Array.isArray(jobs)) return [];
  const seen = new Set();
  return jobs.filter((job) => {
    const id = job?._id != null ? String(job._id) : '';
    if (!id || seen.has(id)) return false;
    if (!isJobTimerActive(job)) return false;
    seen.add(id);
    return true;
  });
};

/**
 * Get display status for a job with proper translation and pause detection
 * @param {Object} job - The job object
 * @returns {string} - Translated status string
 */
export const getJobDisplayStatus = (job) => {
  if (!job) return '-';
  
  const status = job.status || '';
  const travelStartTime = job.travelStartTime;
  const workStartTime = job.workStartTime;
  
  // Status translations
  const statusTranslations = {
    'In Pending': 'Na čekanju',
    'Received': 'Primljeno',
    'Diagnosing': 'Dijagnostika',
    'Waiting for Parts': 'Čeka delove',
    'In Repair': 'U popravci',
    'In Progress': 'U toku',
    'On Road': 'U putu',
    'At Client': 'Kod stranke',
    'Completed': 'Završeno',
    'Finished': 'Završeno',
    'Delivered': 'Isporučeno',
    'Cancelled': 'Otkazano',
    'Archived': 'Arhivirano'
  };
  
  // Check if travel is paused (status is 'On Road' but travelStartTime is null)
  if (status === 'On Road' && !travelStartTime) {
    return 'Pauziran put';
  }
  
  // Check if work is paused (status is 'In Repair' or 'In Progress' but workStartTime is null)
  if ((status === 'In Repair' || status === 'In Progress') && !workStartTime) {
    return 'Pauziran rad';
  }
  
  // If travel is active (travelStartTime exists), show "U putu"
  if (travelStartTime) {
    return 'U putu';
  }
  
  // If work is active (workStartTime exists), show appropriate work status
  if (workStartTime) {
    if (status === 'In Repair' || status === 'In Progress' || status === 'At Client') {
      return statusTranslations[status] || status;
    }
    return 'U radu';
  }
  
  // Default: translate the status
  return statusTranslations[status] || status;
};

/**
 * Parsira iznos novca u broj (RSD često: "138.000", "1.234.567,89").
 * Ne koristiti samo parseFloat: parseFloat("138.000") === 138 (pogrešno).
 */
export const parseMoneyAmountSR = (raw) => {
  if (raw == null || raw === '') return 0;
  if (typeof raw === 'number' && !Number.isNaN(raw)) return raw;

  let s = String(raw).trim();
  if (!s) return 0;

  const neg = s.startsWith('-');
  if (neg) s = s.slice(1);

  s = s.replace(/\s/g, '').replace(/RSD|rsd|din\.?/gi, '');
  s = s.replace(/[^\d.,]/g, '');
  if (!s) return 0;

  if (s.includes(',')) {
    const idx = s.lastIndexOf(',');
    const intPart = s.slice(0, idx).replace(/\./g, '');
    const decPart = s.slice(idx + 1).replace(/[^\d]/g, '');
    const n = parseFloat(`${intPart}.${decPart}`) || 0;
    return neg ? -n : n;
  }

  const parts = s.split('.');
  if (parts.length > 1) {
    const last = parts[parts.length - 1];
    if (last.length === 3 && /^\d{3}$/.test(last)) {
      const n = parseFloat(parts.join('')) || 0;
      return neg ? -n : n;
    }
  }

  const n = parseFloat(s) || 0;
  return neg ? -n : n;
};

/**
 * Datum za raspored u arhivi / analitici (isti redosled kao ArchiveView kalendar).
 * completedAt = datum završetka/naplate u izveštaju (može se ručno izmeniti u arhivi).
 * archivedAt samo ako nema datuma završetka.
 */
export const getArchivedJobTimelineDate = (job) => {
  if (!job) return new Date(NaN);
  const raw =
    job.report?.completedAt ||
    job.report?.submittedAt ||
    job.archivedAt ||
    job.serviceDate ||
    job.updatedAt ||
    job.createdAt;
  return raw ? new Date(raw) : new Date(NaN);
};

/**
 * Lokalni kalendar dan kao YYYY-MM-DD.
 * Ne koristiti date.toISOString().split('T')[0] za ćelije kalendara — u CET/CEST to pomera dan u odnosu na getDate().
 */
export const formatLocalDateKey = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

/** Kalendarski dan u lokalnoj zoni YYYY-MM-DD (isti ključ kao formatLocalDateKey(completedAt)) */
export const getArchivedJobLocalDayKey = (job) => {
  const d = getArchivedJobTimelineDate(job);
  if (Number.isNaN(d.getTime())) return null;
  return formatLocalDateKey(d);
};

/** Filter po opsegu – kao ArchiveView (UTC od lokalnog kalendarskog dana) */
export const getArchivedJobDateOnlyForFilter = (job) => {
  const d = getArchivedJobTimelineDate(job);
  if (Number.isNaN(d.getTime())) return null;
  return new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
};